import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

interface HealthResponse {
  status: string;
  message: string;
  services: Record<string, string>;
}

const AdminDashboard: React.FC = () => {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkHealth();
  }, []);

  const checkHealth = async () => {
    try {
      setLoading(true);
      const response = await axios.get<HealthResponse>(`${API_BASE_URL}/api/health/`);
      setHealth(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to connect to backend');
      console.error('Health check failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const testProtectedRoute = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE_URL}/api/protected`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(`Protected route test successful: ${response.data.message}`);
    } catch (err) {
      alert('Protected route test failed - please login first');
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="page-header">
        <h1>Budget App - System Dashboard (v2.1)</h1>
        <p className="page-subtitle">System health monitoring and development progress</p>
      </div>

      {/* Authentication Status */}
      <div className="card">
        <h2 className="card-title">Authentication Status</h2>
        <p>Welcome, test@example.com! (test@example.com)</p>
        <button onClick={testProtectedRoute} className="btn btn-primary">
          Test Protected Route
        </button>
      </div>

      {/* Backend Connection Status */}
      {error && (
        <div className="card error-card">
          <h2 className="card-title">⚠️ Backend Connection</h2>
          <p>❌ Failed to connect to backend</p>
          <button onClick={checkHealth} className="btn btn-primary">Retry</button>
        </div>
      )}

      {health && (
        <div className="card">
          <div className="card-content">
            <h2 className="card-title">System Status: {health.status.toUpperCase()}</h2>
            <p className="card-subtitle">{health.message}</p>

            <div className="services-grid">
              {Object.entries(health.services).map(([service, status]) => (
                <div key={service} className="service-item">
                  <span className="service-name">{service}</span>
                  <span className={`service-status ${status === 'connected' ? 'success' : 'error'}`}>
                    {status === 'connected' ? '✅' : '❌'} {status}
                  </span>
                </div>
              ))}
            </div>

            <div className="health-actions">
              <button onClick={checkHealth} className="btn btn-primary">
                Refresh Status
              </button>
              <a href={`${API_BASE_URL}/api/health/database`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                Database Details
              </a>
              <a href={`${API_BASE_URL}/api/health/opensearch`} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                OpenSearch Details
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Development Progress */}
      <div className="card">
        <h2 className="card-title">Development Progress</h2>
        <div className="progress-list">
          <div className="progress-item completed">
            <span className="progress-icon">✅</span>
            <span>Basic React frontend with NextFed design system</span>
          </div>
          <div className="progress-item completed">
            <span className="progress-icon">✅</span>
            <span>FastAPI backend with authentication</span>
          </div>
          <div className="progress-item completed">
            <span className="progress-icon">✅</span>
            <span>AWS RDS connection</span>
          </div>
          <div className="progress-item completed">
            <span className="progress-icon">✅</span>
            <span>AWS OpenSearch connection</span>
          </div>
          <div className="progress-item completed">
            <span className="progress-icon">✅</span>
            <span>Modern navigation with brand styling</span>
          </div>
          <div className="progress-item completed">
            <span className="progress-icon">✅</span>
            <span>Health monitoring system</span>
          </div>
          <div className="progress-item completed">
            <span className="progress-icon">✅</span>
            <span>React Router implementation</span>
          </div>
          <div className="progress-item in-progress">
            <span className="progress-icon">🔄</span>
            <span>Presidential Budget analysis features</span>
          </div>
          <div className="progress-item pending">
            <span className="progress-icon">⚪</span>
            <span>AWS Cognito authentication integration</span>
          </div>
          <div className="progress-item pending">
            <span className="progress-icon">⚪</span>
            <span>Congressional Acts analysis features</span>
          </div>
          <div className="progress-item pending">
            <span className="progress-icon">⚪</span>
            <span>Investment Spending analysis features</span>
          </div>
          <div className="progress-item pending">
            <span className="progress-icon">⚪</span>
            <span>Depot Maintenance analysis features</span>
          </div>
          <div className="progress-item pending">
            <span className="progress-icon">⚪</span>
            <span>Market Creation analysis features</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
